// @dynamic

import FastdomPromised from 'fastdom/extensions/fastdom-promised';
import fastdom from 'fastdom';
import { exists } from '../functions/exists';
import { memoize } from '../modules/shared/decorators/memoize';

type MaxTotalColumnWidths = {
  maxWidthPerCol: number[];
  total: number;
};

export type BestColumnFit = { optimalNumCols: number; maxWidthPerCol: number[]; willOverflowX: boolean };

export class HtmlUtils {
  static readonly fdp = fastdom.extend(FastdomPromised);

  public static getElementTotalHeight(nativeElement: any): number {
    if (exists(nativeElement)) {
      const computedStyle = window.getComputedStyle(nativeElement);
      const marginTop = parseFloat(computedStyle?.marginTop?.replace('px', ''));
      const marginBottom = parseFloat(computedStyle?.marginBottom?.replace('px', ''));
      const paddingTop = parseFloat(computedStyle?.paddingTop?.replace('px', ''));
      const paddingBottom = parseFloat(computedStyle?.paddingBottom?.replace('px', ''));
      const borderTop = parseFloat(computedStyle?.borderTopWidth?.replace('px', ''));
      const borderBottom = parseFloat(computedStyle?.borderBottomWidth?.replace('px', ''));
      const zoom = parseFloat(nativeElement?.style?.zoom || '1');
      // Don't use nativeElement.offsetHeight because it doesn't give you the decimal value.
      // Window.getComputedStyle(nativeElement).getPropertyValue('height') gives you the decimal value.
      // Without the decimal your heights will be off.
      const height = parseFloat(computedStyle.getPropertyValue('height')) || 0;
      const boxSizing = computedStyle?.boxSizing;
      // border-box tells the browser to account for any border and padding in the values you specify for an element's
      // width and height. If you set an element's width to 100 pixels, that 100 pixels will include any border or
      // padding you added, and the content box will shrink to absorb that extra width. This typically makes it much
      // easier to size elements (https://developer.mozilla.org/en-US/docs/Web/CSS/box-sizing).
      // Therefore, we need to strip out padding and border if the box-sizing is border-box, because it's already
      // accounted for in the height.
      const padding = boxSizing !== 'border-box' ? paddingTop + paddingBottom : 0;
      const border = boxSizing !== 'border-box' ? borderTop + borderBottom : 0;
      const margin = marginTop + marginBottom;
      const totalHeight = height + padding + border + margin;
      return zoom * totalHeight;
    } else {
      return 0;
    }
  }

  static async getElementTotalHeightAsync(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => HtmlUtils.getElementTotalHeight(nativeElement));
  }

  public static getElementPaddingAndBorderHeight(nativeElement: any): number {
    if (exists(nativeElement)) {
      const computedStyle = window.getComputedStyle(nativeElement);
      const paddingTop = parseFloat(computedStyle?.paddingTop?.replace('px', ''));
      const paddingBottom = parseFloat(computedStyle?.paddingBottom?.replace('px', ''));
      const borderTop = parseFloat(computedStyle?.borderTopWidth?.replace('px', ''));
      const borderBottom = parseFloat(computedStyle?.borderBottomWidth?.replace('px', ''));
      const zoom = parseFloat(nativeElement?.style?.zoom || '1');
      const padding = paddingTop + paddingBottom;
      const border = borderTop + borderBottom;
      return zoom * (padding + border);
    } else {
      return 0;
    }
  }

  static async getElementPaddingAndBorderHeightAsync(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => HtmlUtils.getElementPaddingAndBorderHeight(nativeElement));
  }

  public static getElementTopMargin(nativeElement: any): number {
    if (exists(nativeElement)) {
      const marginTop = parseFloat(window.getComputedStyle(nativeElement)?.marginTop?.replace('px', ''));
      const zoom = parseFloat(nativeElement?.style?.zoom || '1');
      return zoom * marginTop;
    } else {
      return 0;
    }
  }

  static getElementTopMarginAsync(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => HtmlUtils.getElementTopMargin(nativeElement));
  }

  public static getElementBottomMargin(nativeElement: any): number {
    if (exists(nativeElement)) {
      const marginBottom = parseFloat(window.getComputedStyle(nativeElement)?.marginBottom?.replace('px', ''));
      const zoom = parseFloat(nativeElement?.style?.zoom || '1');
      return zoom * marginBottom;
    } else {
      return 0;
    }
  }

  static getElementBottomMarginAsync(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => HtmlUtils.getElementBottomMargin(nativeElement));
  }

  public static getElementHeightWithoutMargins(nativeElement: any): number {
    return (
      HtmlUtils.getElementTotalHeight(nativeElement) -
      HtmlUtils.getElementTopMargin(nativeElement) -
      HtmlUtils.getElementBottomMargin(nativeElement)
    );
  }

  static async getElementHeightWithoutMarginsAsync(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => HtmlUtils.getElementHeightWithoutMargins(nativeElement));
  }

  static async getZoomLevel(nativeElement: any): Promise<number> {
    return HtmlUtils.fdp.measure(() => {
      return parseFloat(nativeElement?.style?.zoom || '1');
    });
  }

  @memoize
  static pxToNumber(v: string): number {
    const num = parseFloat(v || '0');
    return Number.isFinite(num) ? num : 0;
  }

  @memoize
  static computeColumnWidthsAndTotal(widthsPx: number[], numCols: number, colGapPx: number): MaxTotalColumnWidths {
    const maxWidthPerCol: number[] = new Array(numCols).fill(0);
    for (let i = 0; i < widthsPx.length; i++) {
      const col = i % numCols;
      const w = widthsPx[i];
      if (w > maxWidthPerCol[col]) maxWidthPerCol[col] = w;
    }
    const sumCols = maxWidthPerCol.reduce((a, b) => a + b, 0);
    const gaps = numCols > 1 ? colGapPx * (numCols - 1) : 0;
    return { maxWidthPerCol, total: sumCols + gaps };
  }

  @memoize
  static findBestColumns(widthsPx: number[], containerWidth: number, colGapPx: number): BestColumnFit {
    // Binary search to find optimal number of columns
    // search range is 1 -> number of all grid items
    const widestItem: number = widthsPx.length ? Math.max(...widthsPx) : 0;
    const willOverflowX: boolean = widestItem > containerWidth;
    let lo: number = 1;
    let hi: number = Math.max(1, widthsPx.length);
    let best: { optimalNumCols: number; maxWidthPerCol: number[] } | null = null;
    while (lo <= hi) {
      const mid: number = lo + Math.floor((hi - lo) / 2);
      const { maxWidthPerCol, total } = this.computeColumnWidthsAndTotal(widthsPx, mid, colGapPx);
      const fits: boolean = total <= containerWidth;

      if (fits) {
        best = { optimalNumCols: mid, maxWidthPerCol: maxWidthPerCol };
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (!best) {
      const { maxWidthPerCol } = this.computeColumnWidthsAndTotal(widthsPx, 1, colGapPx);
      best = { optimalNumCols: 1, maxWidthPerCol: maxWidthPerCol };
    }
    return { ...best, willOverflowX: willOverflowX };
  }
}
